// Pages/inventario/components/InventarioMovimientosTab.tsx
//
// Pestaña "Movimientos" de /inventario — LA BITÁCORA (round 2026-08-06).
//
// Por qué existe: la información de "qué pasó con las cosas" estaba, pero
// repartida de una forma que solo servía si uno YA sabía la respuesta.
//
//   • Finanzas lista transacciones, pero de UN MES: para encontrar el
//     movimiento de un producto había que acordarse primero de en qué mes fue.
//   • La línea de tiempo cuenta la historia de UN producto, y hay que abrirla
//     de a uno.
//   • Las huérfanas son una excepción, no una vista.
//
// Ninguna respondía "¿qué entró y salió de esta propiedad?" ni "¿dónde anduvo
// esto?", que son las preguntas que uno tiene de verdad al hacer seguimiento.
//
// Decisiones de diseño:
//   - SIN filtro de fecha por defecto. Un rango preseleccionado esconde
//     movimientos sin decirlo: el admin ve una lista corta y concluye que no
//     hay más. Se abre mostrando todo, con el total a la vista para que sea
//     evidente cuánto hay que acotar.
//   - El filtro por lugar matchea ORIGEN **o** DESTINO. Uno no piensa "salidas
//     de la bodega" y "entradas a la bodega" por separado: piensa "lo que pasó
//     por la bodega".
//   - El resumen se calcula server-side sobre TODO el conjunto filtrado, no
//     sobre la página. Un total que cambia al pasar de página es una mentira
//     difícil de notar.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ArrowRightOutlined,
  HistoryOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import { inventarioApi, MovimientosFiltros } from "../../../services/inventario.api";
import { CATEGORIA_LABEL } from "../../flujos/flujos.api";
import {
  useFiltroPersistido,
  useRangoPersistido,
} from "../../../hooks/useFiltroPersistido";
import { UbicacionTag } from "./ProductoCells";
import { formatPropertyName } from "../../../utils/propertyDisplay";
import HistoriaProductoDrawer from "./HistoriaProductoDrawer";
import "../inventario.css";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const CLP = (n: number | null | undefined) =>
  `$${Number(n || 0).toLocaleString("es-CL")}`;

interface MovimientoRow {
  id: number;
  tipo: "compra" | "movimiento";
  fecha: string;
  productoNombre: string;
  categoria: string | null;
  inventarioId: number | null;
  huerfana: boolean;
  cantidadUnidades: number;
  esPack: boolean;
  cantidadPacks: number;
  unidadesPorPack: number;
  precioPorUnidad: number;
  precioPorPack: number;
  montoTotal: number;
  origenTipo: string | null;
  origenId: number | null;
  origenNombre: string | null;
  destinoTipo: string;
  destinoId: number;
  destinoNombre: string | null;
  registradoPor: string | null;
}

interface Resumen {
  compras: number;
  movimientos: number;
  montoCompras: number;
  montoMovimientos: number;
}

/** Una ubicación elegible en el filtro. */
export interface UbicacionOpcion {
  id: number;
  nombre: string;
}

interface Props {
  /** Listas ya cargadas por la página (no se vuelven a pedir). */
  propiedades: UbicacionOpcion[];
  bodegas: UbicacionOpcion[];
  bodeguitas: UbicacionOpcion[];
}

/** Valor del `<Select>` de lugar: `"<tipo>#<id>"`, o vacío = todos. */
const LUGAR_TODOS = "";

/** Forma que tiene que tener un lugar guardado para seguir sirviendo. */
const ES_LUGAR = /^(propiedades|bodegas|bodeguitas)#\d+$/;

/**
 * Columnas por las que el SERVIDOR sabe ordenar la bitácora. Restaurar una
 * clave que ya no existe dejaría la tabla pidiendo un orden que el backend
 * ignora, sin flecha que lo delate.
 */
const COLUMNAS_ORDENABLES = new Set(["id", "fecha", "tipo", "producto", "monto"]);

const InventarioMovimientosTab: React.FC<Props> = ({
  propiedades,
  bodegas,
  bodeguitas,
}) => {
  const [rows, setRows] = useState<MovimientoRow[]>([]);
  const [resumen, setResumen] = useState<Resumen | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  /**
   * Orden de la bitácora. SERVER-SIDE, igual que en la tabla de productos: con
   * paginación en el servidor, ordenar en el navegador ordenaría las 50 filas
   * de la página y no la bitácora — y se vería correcto. Quien ordena por monto
   * para hallar la compra más cara vería el máximo de su página y lo tomaría
   * por el máximo de todo.
   *
   * El default (fecha, lo más reciente primero) es cómo se lee una bitácora.
   */
  const [orden, setOrden] = useFiltroPersistido<{
    columna: string | null;
    direccion: "ascend" | "descend" | null;
  }>(
    "inventario.movimientos.orden",
    { columna: null, direccion: null },
    {
      validar: (v) => {
        if (!v || typeof v !== "object") return false;
        const { columna, direccion } = v as {
          columna: unknown;
          direccion: unknown;
        };
        if (columna === null && direccion === null) return true;
        if (direccion !== "ascend" && direccion !== "descend") return false;
        return typeof columna === "string" && COLUMNAS_ORDENABLES.has(columna);
      },
    },
  );
  // El rango caduca a los 7 días (lo pone `useRangoPersistido`): es una ventana
  // ABSOLUTA, y volver un mes después a una bitácora acotada a otras fechas se
  // lee como "no pasó nada", no como "hay un filtro puesto".
  const [rango, setRango] = useRangoPersistido("inventario.movimientos.rango", null);
  const [tipo, setTipo] = useFiltroPersistido<"" | "compra" | "movimiento">(
    "inventario.movimientos.tipo",
    "",
    { validar: (v) => v === "" || v === "compra" || v === "movimiento" },
  );
  const [lugar, setLugar] = useFiltroPersistido<string>(
    "inventario.movimientos.lugar",
    LUGAR_TODOS,
    { validar: (v) => v === LUGAR_TODOS || (typeof v === "string" && ES_LUGAR.test(v)) },
  );
  const [categoria, setCategoria] = useFiltroPersistido<string>(
    "inventario.movimientos.categoria",
    "",
    {
      validar: (v) =>
        v === "" || v === "sin" || (typeof v === "string" && v in CATEGORIA_LABEL),
    },
  );
  // `texto` es lo que se escribe; `busqueda` es lo que se consultó. Separarlos
  // evita disparar una consulta por tecla y deja el Enter como gesto explícito
  // de "busca esto".
  //
  // Se recuerda SOLO lo aplicado, y el campo arranca mostrándolo. Guardar
  // además el borrador dejaba el buscador con un término escrito que NO estaba
  // filtrando nada, y sin salida visible: `hayFiltros` mira `busqueda`, así que
  // el botón "Limpiar filtros" ni se dibujaba. Mismo criterio que los demás
  // buscadores del proyecto (/clientes, /configuración, /actividad-clientes).
  const [busqueda, setBusqueda] = useFiltroPersistido(
    "inventario.movimientos.busqueda",
    "",
    { validar: (v) => typeof v === "string" },
  );
  const [texto, setTexto] = useState<string>(busqueda);
  // El hook puede releer lo guardado DESPUÉS de montar (la identidad del
  // usuario llega tarde), así que el campo sigue a `busqueda` en vez de
  // copiarla una sola vez.
  useEffect(() => {
    setTexto(busqueda);
  }, [busqueda]);

  const [historia, setHistoria] = useState<{ id: number; nombre: string } | null>(
    null,
  );

  const opcionesLugar = useMemo(
    () => [
      {
        label: "Bodegas",
        options: bodegas.map((b) => ({
          value: `bodegas#${b.id}`,
          label: b.nombre,
        })),
      },
      {
        label: "Bodeguitas",
        options: bodeguitas.map((b) => ({
          value: `bodeguitas#${b.id}`,
          label: b.nombre,
        })),
      },
      {
        label: "Propiedades",
        options: propiedades.map((p) => ({
          value: `propiedades#${p.id}`,
          // El nombre de propiedad va SIEMPRE en mayúsculas al mostrarse (regla
          // del proyecto). El `value` conserva el dato crudo, que es lo que se
          // filtra y se manda al backend.
          label: formatPropertyName(p.nombre),
        })),
      },
    ],
    [bodegas, bodeguitas, propiedades],
  );

  const cargar = useCallback(async () => {
    setLoading(true);
    const filtros: MovimientosFiltros = { page, size };
    if (orden.columna && orden.direccion) {
      filtros.ordenarPor = orden.columna;
      filtros.ordenDireccion = orden.direccion === "descend" ? "desc" : "asc";
    }
    const desde = rango?.[0];
    const hasta = rango?.[1];
    if (desde) filtros.desde = desde.format("YYYY-MM-DD");
    if (hasta) filtros.hasta = hasta.format("YYYY-MM-DD");
    if (tipo) filtros.tipo = tipo;
    if (categoria) filtros.categoria = categoria;
    if (busqueda) filtros.q = busqueda;
    if (lugar) {
      const [t, id] = lugar.split("#");
      filtros.ubicacionTipo = t as MovimientosFiltros["ubicacionTipo"];
      filtros.ubicacionId = Number(id);
    }

    const res = await inventarioApi.getMovimientos(filtros);
    // `apiFetch` no lanza: devuelve null y ya mostró el toast. Dejamos la tabla
    // como estaba en vez de vaciarla, que se leería como "no hay movimientos".
    if (res?.data) {
      setRows(res.data.movimientos ?? []);
      setTotal(Number(res.data.total) || 0);
      setResumen(res.data.resumen ?? null);
    }
    setLoading(false);
  }, [page, size, rango, tipo, categoria, busqueda, lugar, orden]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  /** Cualquier cambio de filtro vuelve a la página 1: si no, se queda en una
   *  página que el nuevo filtro ya no tiene y la tabla sale vacía. */
  const conReset = <T,>(setter: (v: T) => void) => (valor: T) => {
    setPage(1);
    setter(valor);
  };

  const limpiar = () => {
    setPage(1);
    setRango(null);
    setTipo("");
    setLugar(LUGAR_TODOS);
    setCategoria("");
    setTexto("");
    setBusqueda("");
  };

  const hayFiltros = Boolean(rango || tipo || lugar || categoria || busqueda);

  /**
   * Marca una columna como ordenable por el SERVIDOR.
   *
   * `sortOrder` explícito no es opcional: la tabla se remonta con cada
   * respuesta, así que sin él la flechita se perdería justo después de ordenar.
   *
   * Las columnas "Cantidad" y "Recorrido" NO son ordenables y es a propósito:
   * la primera muestra packs o unidades según la fila (dos números distintos en
   * la misma columna) y la segunda es origen Y destino juntos. Ordenar por
   * cualquiera de las dos daría un resultado que no corresponde a lo que se ve.
   */
  const ordenable = (key: string) => ({
    sorter: true,
    sortOrder: (orden.columna === key ? orden.direccion : null) as
      | "ascend"
      | "descend"
      | null,
  });

  const columns = [
    {
      title: "Código",
      dataIndex: "id",
      key: "id",
      width: 90,
      ...ordenable("id"),
      render: (id: number) => (
        <Tooltip title="Código de la transacción. Estable e histórico: se puede buscar acá mismo escribiéndolo.">
          <Tag className="inventario-id-tag">#{id}</Tag>
        </Tooltip>
      ),
    },
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      width: 110,
      ...ordenable("fecha"),
    },
    {
      title: "Tipo",
      dataIndex: "tipo",
      key: "tipo",
      width: 120,
      ...ordenable("tipo"),
      render: (t: string) =>
        t === "compra" ? (
          <Tag color="green">Compra</Tag>
        ) : (
          <Tag color="geekblue">Movimiento</Tag>
        ),
    },
    {
      title: "Producto",
      dataIndex: "productoNombre",
      key: "producto",
      ...ordenable("producto"),
      render: (nombre: string, r: MovimientoRow) => (
        <div className="inventario-mov-producto">
          <span>{nombre}</span>
          <div className="inventario-mov-producto-meta">
            {r.inventarioId ? (
              <Tag className="inventario-id-tag">PROD #{r.inventarioId}</Tag>
            ) : null}
            {r.categoria ? (
              <Tag color="geekblue" className="inventario-tag-mini">
                {CATEGORIA_LABEL[r.categoria] ?? r.categoria}
              </Tag>
            ) : null}
            {r.huerfana ? (
              <Tooltip title="El producto de esta transacción ya no existe en el inventario. Suele pasar cuando un movimiento vació la ubicación de origen: la mercadería existe (está en el destino), pero esta compra quedó sin ficha a la que colgarse.">
                <Tag color="orange" className="inventario-tag-mini">
                  sin producto
                </Tag>
              </Tooltip>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      title: "Cantidad",
      key: "cantidad",
      width: 150,
      render: (_: unknown, r: MovimientoRow) =>
        r.esPack ? (
          <span>
            {r.cantidadPacks} pack{r.cantidadPacks === 1 ? "" : "s"}
            <Text type="secondary"> ({r.cantidadUnidades} u.)</Text>
          </span>
        ) : (
          <span>
            {r.cantidadUnidades} u<Text type="secondary">nidades</Text>
          </span>
        ),
    },
    {
      title: "Recorrido",
      key: "recorrido",
      render: (_: unknown, r: MovimientoRow) => (
        <div className="inventario-mov-recorrido">
          <UbicacionTag tipo={r.origenTipo} nombre={r.origenNombre} />
          <ArrowRightOutlined className="inventario-mov-flecha" />
          <UbicacionTag tipo={r.destinoTipo} nombre={r.destinoNombre} />
        </div>
      ),
    },
    {
      title: "Monto",
      dataIndex: "montoTotal",
      key: "monto",
      width: 120,
      align: "right" as const,
      ...ordenable("monto"),
      render: (m: number) => CLP(m),
    },
    {
      title: "Registró",
      dataIndex: "registradoPor",
      key: "registradoPor",
      width: 140,
      render: (v: string | null) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "",
      key: "acciones",
      width: 56,
      render: (_: unknown, r: MovimientoRow) =>
        r.inventarioId ? (
          <Tooltip title="Ver la historia completa de este producto">
            <Button
              icon={<HistoryOutlined />}
              onClick={() =>
                setHistoria({ id: r.inventarioId as number, nombre: r.productoNombre })
              }
            />
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={8}>
            <RangePicker
              style={{ width: "100%" }}
              value={rango}
              onChange={(v) =>
                conReset<[Dayjs, Dayjs] | null>(setRango)(
                  v && v[0] && v[1] ? [v[0], v[1]] : null,
                )
              }
              placeholder={["Desde", "Hasta"]}
              allowClear
            />
          </Col>
          <Col xs={24} md={8}>
            <Select
              style={{ width: "100%" }}
              value={lugar}
              onChange={conReset<string>(setLugar)}
              options={[
                { value: LUGAR_TODOS, label: "Todos los lugares" },
                ...opcionesLugar,
              ]}
              showSearch
              optionFilterProp="label"
              placeholder="Lugar (origen o destino)"
            />
          </Col>
          <Col xs={24} md={8}>
            <Input
              placeholder="Buscar producto, lugar o código (#412)"
              prefix={<SearchOutlined />}
              allowClear
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value);
                // Limpiar con la X tiene que devolver los resultados de
                // inmediato: obligar a un Enter extra después de vaciar el
                // campo se siente como que el filtro quedó pegado.
                if (!e.target.value) {
                  setPage(1);
                  setBusqueda("");
                }
              }}
              onPressEnter={() => {
                setPage(1);
                setBusqueda(texto.trim());
              }}
            />
          </Col>
          <Col xs={24} md={10}>
            <Segmented
              value={tipo}
              onChange={(v) => conReset<any>(setTipo)(v)}
              options={[
                { label: "Todo", value: "" },
                { label: "Compras", value: "compra" },
                { label: "Movimientos", value: "movimiento" },
              ]}
            />
          </Col>
          <Col xs={24} md={8}>
            <Select
              style={{ width: "100%" }}
              value={categoria}
              onChange={conReset<string>(setCategoria)}
              options={[
                { value: "", label: "Todas las categorías" },
                { value: "sin", label: "Sin categoría" },
                ...Object.entries(CATEGORIA_LABEL).map(([value, label]) => ({
                  value,
                  label: String(label),
                })),
              ]}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} md={6} style={{ textAlign: "right" }}>
            <Space>
              {hayFiltros && <Button onClick={limpiar}>Limpiar filtros</Button>}
              <Tooltip title="Volver a consultar">
                <Button icon={<ReloadOutlined />} onClick={() => void cargar()} />
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </Card>

      {resumen && (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Movimientos en total" value={total} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Compras" value={resumen.compras} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Tooltip title="Lo que costó comprar todo lo que entra en este filtro. Es el egreso real de Aloja Fácil — no descuenta lo que después se le cobre al cliente.">
                <div>
                  <Statistic
                    title="Gasto en compras"
                    value={CLP(resumen.montoCompras)}
                  />
                </div>
              </Tooltip>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Traslados" value={resumen.movimientos} />
            </Card>
          </Col>
        </Row>
      )}

      <Spin spinning={loading}>
        <Table
          rowKey="id"
          dataSource={rows}
          columns={columns}
          size="small"
          scroll={{ x: 1100 }}
          // El `extra.action` no es opcional: AntD dispara este mismo handler
          // para ordenar, filtrar Y paginar. Sin distinguirlo, cada clic en un
          // número de página entraría acá y `cambiarOrden` lo devolvería a la
          // página 1 — la tabla quedaría atrapada en la primera. Ya pasó en la
          // tabla de productos.
          onChange={(_p, _f, sorter, extra) => {
            if (extra?.action !== "sort") return;
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            const columna = (s?.order ? (s?.columnKey as string) : null) ?? null;
            const direccion = (s?.order as "ascend" | "descend" | null) ?? null;
            if (orden.columna === columna && orden.direccion === direccion) return;
            setOrden({ columna, direccion });
            setPage(1);
          }}
          locale={{
            emptyText: (
              <Empty
                description={
                  hayFiltros
                    ? "Ningún movimiento coincide con estos filtros."
                    : "Todavía no hay compras ni movimientos registrados."
                }
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize: size,
            total,
            showSizeChanger: true,
            pageSizeOptions: ["25", "50", "100", "200"],
            showTotal: (t, [a, b]) => `${a}–${b} de ${t}`,
            onChange: (p, ps) => {
              // Cambiar el tamaño de página vuelve a la 1: con otro tamaño, la
              // página en la que uno estaba ya no contiene lo mismo, y quedarse
              // ahí muestra un tramo arbitrario de la lista.
              setPage(ps !== size ? 1 : p);
              setSize(ps);
            },
          }}
        />
      </Spin>

      <HistoriaProductoDrawer
        inventarioId={historia?.id ?? null}
        open={historia !== null}
        onClose={() => setHistoria(null)}
        nombreFallback={historia?.nombre ?? null}
      />
    </div>
  );
};

export default InventarioMovimientosTab;
